import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import { API } from '../config';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ResponsiveLogo } from '../components/ThemeLogo';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  ArrowLeft, Plus, FileText, Eye, Pencil, Trash2, Copy,
  Archive, Star, Moon, Sun, Search, Image, Upload, X,
  ChevronLeft, ChevronRight, BarChart3, Clock, Globe,
  Loader2, ExternalLink,
} from 'lucide-react';

const CATEGORIES = [
  "Strategy", "Business Growth", "Leadership", "Decision Making",
  "Execution", "Startups", "Product", "Customer Success",
  "Case Studies", "Odapto Updates"
];

const STATUS_COLORS = {
  draft: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
  published: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  archived: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'code-block'],
    ['link', 'image', 'video'],
    [{ align: [] }],
    ['clean'],
  ],
};

// ── Stats Cards ──
function StatsCards({ stats }) {
  const cards = [
    { label: 'Total', value: stats.total, icon: FileText, color: 'text-foreground' },
    { label: 'Published', value: stats.published, icon: Globe, color: 'text-emerald-500' },
    { label: 'Drafts', value: stats.draft, icon: Pencil, color: 'text-zinc-500' },
    { label: 'Scheduled', value: stats.scheduled, icon: Clock, color: 'text-blue-500' },
    { label: 'Archived', value: stats.archived, icon: Archive, color: 'text-amber-500' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="cms-stats">
      {cards.map(c => (
        <div key={c.label} className="p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center gap-2 mb-1">
            <c.icon className={`w-4 h-4 ${c.color}`} />
            <span className="text-xs text-muted-foreground">{c.label}</span>
          </div>
          <p className="text-2xl font-bold">{c.value}</p>
        </div>
      ))}
    </div>
  );
}

// ── Blog Table ──
function BlogTable({ blogs, onEdit, onDuplicate, onDelete, onArchive, onToggleFeatured }) {
  if (blogs.length === 0) return (
    <div className="text-center py-16 border border-border rounded-xl bg-card">
      <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
      <p className="font-semibold mb-1">No blog posts yet</p>
      <p className="text-sm text-muted-foreground">Create your first blog post to get started.</p>
    </div>
  );

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-card" data-testid="cms-blog-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="text-left px-4 py-3 font-medium">Title</th>
              <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Category</th>
              <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Author</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Date</th>
              <th className="text-center px-4 py-3 font-medium hidden lg:table-cell">Featured</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {blogs.map(blog => (
              <tr key={blog.blog_id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3">
                  <p className="font-medium truncate max-w-[200px] lg:max-w-[300px]">{blog.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{blog.slug}</p>
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{blog.category}</td>
                <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">{blog.author_name}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[blog.status] || STATUS_COLORS.draft}`}>
                    {blog.status}
                  </span>
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-xs text-muted-foreground">
                  {blog.updated_at ? new Date(blog.updated_at).toLocaleDateString() : '-'}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell text-center">
                  <button onClick={() => onToggleFeatured(blog)} className={`p-1 rounded ${blog.featured ? 'text-amber-500' : 'text-muted-foreground/30 hover:text-amber-500'}`}>
                    <Star className="w-4 h-4" fill={blog.featured ? 'currentColor' : 'none'} />
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {blog.status === 'published' && (
                      <a href={`/blog/${blog.slug}`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-muted" title="View"><ExternalLink className="w-3.5 h-3.5" /></a>
                    )}
                    <button onClick={() => onEdit(blog)} className="p-1.5 rounded hover:bg-muted" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => onDuplicate(blog)} className="p-1.5 rounded hover:bg-muted" title="Duplicate"><Copy className="w-3.5 h-3.5" /></button>
                    <button onClick={() => onArchive(blog)} className="p-1.5 rounded hover:bg-muted" title="Archive"><Archive className="w-3.5 h-3.5" /></button>
                    <button onClick={() => onDelete(blog)} className="p-1.5 rounded hover:bg-muted text-red-500" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Media Library Modal ──
function MediaLibrary({ open, onClose, onSelect }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const res = await apiGet(`/cms/media${params}`);
    if (res.ok) { const d = await res.json(); setItems(d.items || []); }
    setLoading(false);
  }, [search]);

  useEffect(() => { if (open) fetchMedia(); }, [open, fetchMedia]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/cms/media`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('odapto_session_token') || sessionStorage.getItem('odapto_session_token')}` },
        body: formData,
      });
      if (res.ok) {
        toast.success('Uploaded!');
        fetchMedia();
      } else toast.error('Upload failed');
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    const res = await apiDelete(`/cms/media/${id}`);
    if (res.ok) { toast.success('Deleted'); fetchMedia(); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Media Library</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search media..." className="pl-9" />
          </div>
          <label className="cursor-pointer">
            <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
            <Button variant="outline" asChild disabled={uploading}>
              <span>{uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />} Upload</span>
            </Button>
          </label>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <Image className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No media files yet. Upload your first image.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {items.map(item => (
                <div key={item.media_id} className="relative group rounded-lg overflow-hidden border border-border aspect-square bg-muted">
                  <img src={item.url} alt={item.original_name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button onClick={() => { onSelect(item.url); onClose(); }} className="p-2 bg-white rounded-lg text-black text-xs font-medium">Select</button>
                    <button onClick={() => handleDelete(item.media_id)} className="p-2 bg-red-500 rounded-lg text-white text-xs font-medium">Delete</button>
                  </div>
                  <p className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1.5 py-0.5 truncate">{item.original_name}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Blog Editor ──
function BlogEditor({ blog, onSave, onCancel }) {
  const { user } = useAuth();
  const [title, setTitle] = useState(blog?.title || '');
  const [slug, setSlug] = useState(blog?.slug || '');
  const [summary, setSummary] = useState(blog?.summary || '');
  const [body, setBody] = useState(blog?.body || '');
  const [category, setCategory] = useState(blog?.category || 'Odapto Updates');
  const [tags, setTags] = useState((blog?.tags || []).join(', '));
  const [featuredImage, setFeaturedImage] = useState(blog?.featured_image || '');
  const [authorName, setAuthorName] = useState(blog?.author_name || user?.name || '');
  const [status, setStatus] = useState(blog?.status || 'draft');
  const [featured, setFeatured] = useState(blog?.featured || false);
  const [seoTitle, setSeoTitle] = useState(blog?.seo?.meta_title || '');
  const [seoDesc, setSeoDesc] = useState(blog?.seo?.meta_description || '');
  const [seoKeywords, setSeoKeywords] = useState((blog?.seo?.keywords || []).join(', '));
  const [saving, setSaving] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaTarget, setMediaTarget] = useState(null); // 'featured' or 'body'
  const [activeTab, setActiveTab] = useState('content');

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    const payload = {
      title: title.trim(),
      slug: slug.trim() || undefined,
      summary: summary.trim(),
      body,
      category,
      tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      featured_image: featuredImage || null,
      author_name: authorName,
      status,
      featured,
      seo: {
        meta_title: seoTitle || title,
        meta_description: seoDesc || summary,
        keywords: seoKeywords.split(',').map(k => k.trim()).filter(Boolean),
      },
    };
    await onSave(payload);
    setSaving(false);
  };

  const openMedia = (target) => { setMediaTarget(target); setMediaOpen(true); };
  const handleMediaSelect = (url) => {
    if (mediaTarget === 'featured') setFeaturedImage(url);
  };

  return (
    <div data-testid="blog-editor">
      <div className="flex items-center justify-between mb-6">
        <button onClick={onCancel} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ChevronLeft className="w-4 h-4" /> Back to Blogs
        </button>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleSave} disabled={saving} className="bg-odapto-orange hover:bg-odapto-orange-hover text-white" data-testid="save-blog-btn">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Saving...</> : <>{blog ? 'Update' : 'Create'} Blog</>}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        {/* Main Content */}
        <div className="space-y-4">
          <div>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Blog title..." className="text-2xl font-bold h-14 border-0 px-0 shadow-none focus-visible:ring-0 placeholder:text-muted-foreground/50" data-testid="blog-title-input" />
          </div>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="seo">SEO</TabsTrigger>
            </TabsList>
            <TabsContent value="content" className="space-y-4 mt-4">
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Summary</Label>
                <textarea value={summary} onChange={e => setSummary(e.target.value)} placeholder="Brief description..." rows={2} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-odapto-orange/30" data-testid="blog-summary-input" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Body</Label>
                <div className="border border-border rounded-lg overflow-hidden [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-border [&_.ql-toolbar]:bg-muted/50 [&_.ql-container]:border-0 [&_.ql-editor]:min-h-[400px] [&_.ql-editor]:text-sm">
                  <ReactQuill theme="snow" value={body} onChange={setBody} modules={QUILL_MODULES} placeholder="Write your article..." />
                </div>
              </div>
            </TabsContent>
            <TabsContent value="seo" className="space-y-4 mt-4">
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Meta Title</Label>
                <Input value={seoTitle} onChange={e => setSeoTitle(e.target.value)} placeholder={title || 'SEO title...'} />
                <p className="text-[10px] text-muted-foreground mt-1">{(seoTitle || title).length}/60 characters</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Meta Description</Label>
                <textarea value={seoDesc} onChange={e => setSeoDesc(e.target.value)} placeholder={summary || 'SEO description...'} rows={3} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-odapto-orange/30" />
                <p className="text-[10px] text-muted-foreground mt-1">{(seoDesc || summary).length}/160 characters</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Keywords (comma-separated)</Label>
                <Input value={seoKeywords} onChange={e => setSeoKeywords(e.target.value)} placeholder="AI, strategy, business..." />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4 space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Slug</Label>
              <Input value={slug} onChange={e => setSlug(e.target.value)} placeholder="auto-generated-from-title" data-testid="blog-slug-input" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Tags (comma-separated)</Label>
              <Input value={tags} onChange={e => setTags(e.target.value)} placeholder="AI, strategy, growth..." />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Author</Label>
              <Input value={authorName} onChange={e => setAuthorName(e.target.value)} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">Featured Article</Label>
              <button onClick={() => setFeatured(!featured)} className={`w-10 h-5 rounded-full transition-colors relative ${featured ? 'bg-odapto-orange' : 'bg-muted'}`}>
                <div className={`w-4 h-4 rounded-full bg-white shadow-sm absolute top-0.5 transition-all ${featured ? 'left-5' : 'left-0.5'}`} />
              </button>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <Label className="text-xs text-muted-foreground mb-2 block">Featured Image</Label>
            {featuredImage ? (
              <div className="relative rounded-lg overflow-hidden aspect-video bg-muted mb-2">
                <img src={featuredImage} alt="Featured" className="w-full h-full object-cover" />
                <button onClick={() => setFeaturedImage('')} className="absolute top-2 right-2 p-1 bg-black/60 rounded-full text-white"><X className="w-3 h-3" /></button>
              </div>
            ) : null}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => openMedia('featured')}>
                <Image className="w-3 h-3 mr-1" /> Media Library
              </Button>
              <Input placeholder="Or paste URL..." value={featuredImage} onChange={e => setFeaturedImage(e.target.value)} className="flex-1 text-xs h-8" />
            </div>
          </div>
        </div>
      </div>

      <MediaLibrary open={mediaOpen} onClose={() => setMediaOpen(false)} onSelect={handleMediaSelect} />
    </div>
  );
}


// ═══════════════════════════════════════
// MAIN CMS PAGE
// ═══════════════════════════════════════

export default function AdminCMSPage() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [view, setView] = useState('list'); // list | create | edit
  const [stats, setStats] = useState({ total: 0, draft: 0, published: 0, scheduled: 0, archived: 0 });
  const [blogs, setBlogs] = useState([]);
  const [editBlog, setEditBlog] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [mediaOpen, setMediaOpen] = useState(false);

  const fetchStats = async () => {
    const res = await apiGet('/cms/blogs/stats');
    if (res.ok) setStats(await res.json());
  };

  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterStatus) params.set('status', filterStatus);
    if (searchQuery) params.set('search', searchQuery);
    params.set('page', page);
    const res = await apiGet(`/cms/blogs?${params}`);
    if (res.ok) {
      const d = await res.json();
      setBlogs(d.blogs || []);
      setTotalPages(d.pages || 1);
    }
    setLoading(false);
  }, [filterStatus, searchQuery, page]);

  useEffect(() => { fetchStats(); fetchBlogs(); }, [fetchBlogs]);

  const handleCreate = async (payload) => {
    const res = await apiPost('/cms/blogs', payload);
    if (res.ok) {
      toast.success('Blog created!');
      setView('list'); fetchStats(); fetchBlogs();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || 'Failed to create');
    }
  };

  const handleUpdate = async (payload) => {
    const res = await apiPut(`/cms/blogs/${editBlog.blog_id}`, payload);
    if (res.ok) {
      toast.success('Blog updated!');
      setView('list'); setEditBlog(null); fetchStats(); fetchBlogs();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || 'Failed to update');
    }
  };

  const handleEdit = async (blog) => {
    const res = await apiGet(`/cms/blogs/${blog.blog_id}`);
    if (res.ok) { setEditBlog(await res.json()); setView('edit'); }
    else toast.error('Failed to load blog');
  };

  const handleDuplicate = async (blog) => {
    const res = await apiPost(`/cms/blogs/${blog.blog_id}/duplicate`, {});
    if (res.ok) { toast.success('Blog duplicated!'); fetchStats(); fetchBlogs(); }
    else toast.error('Failed to duplicate');
  };

  const handleDelete = async (blog) => {
    if (!window.confirm(`Delete "${blog.title}"?`)) return;
    const res = await apiDelete(`/cms/blogs/${blog.blog_id}`);
    if (res.ok) { toast.success('Deleted'); fetchStats(); fetchBlogs(); }
    else toast.error('Failed to delete');
  };

  const handleArchive = async (blog) => {
    const newStatus = blog.status === 'archived' ? 'draft' : 'archived';
    const res = await apiPut(`/cms/blogs/${blog.blog_id}`, { status: newStatus });
    if (res.ok) { toast.success(newStatus === 'archived' ? 'Archived' : 'Restored to draft'); fetchStats(); fetchBlogs(); }
  };

  const handleToggleFeatured = async (blog) => {
    const res = await apiPut(`/cms/blogs/${blog.blog_id}`, { featured: !blog.featured });
    if (res.ok) { toast.success(blog.featured ? 'Unfeatured' : 'Set as featured'); fetchBlogs(); }
  };

  // ── RENDER ──

  if (view === 'create') return (
    <div className="min-h-screen bg-background">
      <Header theme={theme} toggleTheme={toggleTheme} navigate={navigate} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <BlogEditor onSave={handleCreate} onCancel={() => setView('list')} />
      </div>
    </div>
  );

  if (view === 'edit' && editBlog) return (
    <div className="min-h-screen bg-background">
      <Header theme={theme} toggleTheme={toggleTheme} navigate={navigate} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <BlogEditor blog={editBlog} onSave={handleUpdate} onCancel={() => { setView('list'); setEditBlog(null); }} />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="admin-cms-page">
      <Header theme={theme} toggleTheme={toggleTheme} navigate={navigate} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Title Row */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Blog Management</h1>
            <p className="text-sm text-muted-foreground">Create, edit and manage blog articles</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setMediaOpen(true)}>
              <Image className="w-4 h-4 mr-2" /> Media Library
            </Button>
            <Button className="bg-odapto-orange hover:bg-odapto-orange-hover text-white" onClick={() => setView('create')} data-testid="create-blog-btn">
              <Plus className="w-4 h-4 mr-2" /> New Blog Post
            </Button>
          </div>
        </div>

        <StatsCards stats={stats} />

        {/* Filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setPage(1); }} placeholder="Search blogs..." className="pl-9" />
          </div>
          <div className="flex gap-2">
            {['', 'draft', 'published', 'scheduled', 'archived'].map(s => (
              <button
                key={s}
                onClick={() => { setFilterStatus(s); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filterStatus === s ? 'bg-odapto-orange text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
              >
                {s || 'All'}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-odapto-orange" /></div>
        ) : (
          <BlogTable
            blogs={blogs}
            onEdit={handleEdit}
            onDuplicate={handleDuplicate}
            onDelete={handleDelete}
            onArchive={handleArchive}
            onToggleFeatured={handleToggleFeatured}
          />
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        )}
      </div>

      <MediaLibrary open={mediaOpen} onClose={() => setMediaOpen(false)} onSelect={() => {}} />
    </div>
  );
}


// ── Header component ──
function Header({ theme, toggleTheme, navigate }) {
  return (
    <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="flex items-center gap-2">
            <ResponsiveLogo className="h-8 w-auto" />
          </Link>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">CMS</span>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Blog</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Admin
          </Button>
        </div>
      </div>
    </header>
  );
}
